import type { Session } from './auth';
import type { ProblemDetails } from './types';

/** API origin. Empty means same origin (reverse proxy). */
export const apiBase: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(public status: number, public problem: ProblemDetails, public offline = false) {
    super(problem.detail || problem.title || `Request failed (${status})`);
  }
  get code(): string | undefined { return this.problem.code; }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  download(path: string): Promise<{ blob: Blob; filename: string }>;
}

export function createApi(session: Session, onUnauthorized?: () => void): ApiClient {
  async function send(method: string, path: string, body?: unknown): Promise<Response> {
    let token: string;
    try { token = await session.getToken(); } catch { onUnauthorized?.(); throw new ApiError(401, { title: 'Sign-in required' }); }
    let res: Response;
    try {
      res = await fetch(apiBase + path, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        cache: 'no-store',
      });
    } catch {
      throw new ApiError(0, { title: 'Cannot reach the server', detail: 'Check your connection and try again.' }, true);
    }
    if (res.status === 401) onUnauthorized?.();
    if (!res.ok) {
      let problem: ProblemDetails = { title: res.statusText, status: res.status };
      try { problem = { ...problem, ...(await res.json()) }; } catch { /* non-JSON */ }
      throw new ApiError(res.status, problem);
    }
    return res;
  }
  async function json<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
  return {
    get: async (p) => json(await send('GET', p)),
    post: async (p, b) => json(await send('POST', p, b ?? {})),
    put: async (p, b) => json(await send('PUT', p, b ?? {})),
    patch: async (p, b) => json(await send('PATCH', p, b ?? {})),
    download: async (p) => {
      const res = await send('GET', p);
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      return { blob: await res.blob(), filename: m ? decodeURIComponent(m[1]) : 'download.csv' };
    },
  };
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
